import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Boxes,
  CalendarDays,
  FileSpreadsheet,
  FileText,
  Loader as Loader2,
  CircleAlert as AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { emitToast } from '@/components/ui/Toast';
import { useB2BUser } from './B2BUserContext';
import { ConsignmentStoreReportDocument } from '@/components/consignment/ConsignmentStoreReportDocument';
import { useConsignmentStoreReport } from '@/components/consignment/useConsignmentStoreReport';
import {
  buildExcelBlob,
  buildPdfBlob,
  computeTotals,
  downloadBlob,
  fmtMoney,
  fmtQty,
  logReportDownload,
  reportFileName,
  todayIso,
  type ReportFormat,
} from '@/lib/consignmentStoreReport';

export function B2BMyStockPage() {
  const navigate = useNavigate();
  const { session } = useB2BUser();
  const client = session?.role === 'client' ? session.activeClientRecord : null;
  const [asOf, setAsOf] = useState(todayIso());
  const [busy, setBusy] = useState<ReportFormat | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (session && session.role !== 'client') navigate('/b2b', { replace: true });
  }, [session, navigate]);

  const { report, loading, error, reload } = useConsignmentStoreReport(
    client?.company_id ?? null,
    client?.id ?? null,
    asOf,
  );
  const totals = report ? computeTotals(report) : null;

  const handleDownload = async (format: ReportFormat) => {
    if (!report || !client) return;
    setBusy(format);
    try {
      const blob = format === 'excel'
        ? await buildExcelBlob(report)
        : pdfRef.current ? await buildPdfBlob(pdfRef.current) : null;
      if (!blob) throw new Error();
      downloadBlob(blob, reportFileName(report, format));
      await logReportDownload(client.company_id, client.id, format, report.as_of);
    } catch {
      emitToast(format === 'pdf' ? 'שגיאה ביצירת PDF' : 'שגיאה ביצירת קובץ אקסל', 'error');
    } finally {
      setBusy(null);
    }
  };

  if (!session || !client) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" dir="rtl">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Boxes className="w-5 h-5 text-primary-600" />
            יתרות המלאי שלי
          </h1>
          <p className="text-sm text-gray-500 mt-1">הסחורה שנמצאת אצלכם בקונסיגנציה, לפי מחירון החנות</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="relative">
            <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="date"
              value={asOf}
              max={todayIso()}
              onChange={(e) => e.target.value && setAsOf(e.target.value)}
              aria-label="יתרה נכון לתאריך"
              className="pr-9 pl-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none"
            />
          </label>
          <button
            onClick={() => handleDownload('pdf')}
            disabled={!report || loading || busy !== null}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-blue-200 bg-blue-50 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50 transition-colors"
          >
            {busy === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            PDF
          </button>
          <button
            onClick={() => handleDownload('excel')}
            disabled={!report || loading || busy !== null}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors"
          >
            {busy === 'excel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            אקסל
          </button>
        </div>
      </div>

      {report && totals && !loading && report.lines.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'פריטים', value: report.lines.length.toLocaleString('he-IL') },
            { label: 'יחידות', value: fmtQty(totals.qty) },
            { label: 'שווי לפני מע״מ', value: fmtMoney(totals.beforeVat) },
            { label: 'שווי כולל מע״מ', value: fmtMoney(totals.inclVat), strong: true },
          ].map((c) => (
            <div key={c.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <p className="text-xs text-gray-500 font-medium">{c.label}</p>
              <p className={`text-lg font-bold mt-1 ${c.strong ? 'text-primary-700' : 'text-gray-900'}`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
          <p className="text-sm">טוען יתרות…</p>
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center gap-4 py-16">
          <div className="flex items-start gap-3 px-4 py-3.5 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-700 w-full max-w-sm">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <p className="font-semibold">{error}</p>
          </div>
          <button onClick={reload} className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium">
            <RefreshCw className="w-4 h-4" />
            נסה שנית
          </button>
        </div>
      )}

      {!loading && report && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
          <ConsignmentStoreReportDocument report={report} />
        </div>
      )}

      {report && (
        <div className="fixed top-0 -left-[10000px] w-[900px] pointer-events-none" aria-hidden="true">
          <ConsignmentStoreReportDocument ref={pdfRef} report={report} />
        </div>
      )}
    </div>
  );
}
